import dayjs from "dayjs"
import { formatDate } from "./dateUtils"
import { UserLesson } from "../types"
import { GQLInitiatedBy } from "../graphqlTypes"

interface CancelMessageParams {
  username: string
  lesson: UserLesson
  changeFee: number
  reason: string
  timeZone: string
}

export function getSlackCancelMessage({
  username,
  lesson,
  changeFee,
  reason,
  timeZone,
}: CancelMessageParams) {
  let message = `${username} has canceled his or her lesson `
  message += `with ${lesson.instructor.displayName || lesson.instructor.firstName} on ${formatDate(lesson.details.startTime, timeZone, "long")}.`
  if (reason) {
    message += `\nReason for change: ${reason}`
  }
  if (changeFee > 0) {
    message += `\nThey have agreed to the $${changeFee} change fee. Please charge this in Stripe now and add a manual purchase log charge in Dash.`
  }
  return message
}

export interface CancelPolicy {
  message: string
  informativeMessage?: string
  changeFee: number
}

const CANCEL_POLICIES = {
  "within-72-to-24-hours": (lessonPrice: number): CancelPolicy => {
    const changeFee = lessonPrice * 0.35
    return {
      message: `Your lesson is starting within 72 to 24 hours. According to our cancellation policy, we will charge your card on file a 35% lesson value change fee of $${changeFee.toFixed(2)}. We hope you understand this is necessary to maintain a premium experience for our students and instructors. If you would like to use a different card, please give us a call.`,
      informativeMessage:
        "Changes or cancellations made between 72 hours and 24 hours prior of the lesson start: 35% of the lesson value",
      changeFee,
    }
  },
  "within-24-hours": (lessonPrice: number): CancelPolicy => {
    const changeFee = lessonPrice
    return {
      message: `Your lesson starts within 24 hours. In accordance with our cancellation policy, we will charge the full price of the lesson $${changeFee.toFixed(2)} by expiring your credits.`,
      informativeMessage:
        "Within 24 hours, no shows, or changes or cancellations made after the lesson start: full lesson price",
      changeFee,
    }
  },
  "over-72-hours": (): CancelPolicy => ({
    message:
      "Changes or cancellations made at least 72 hours prior to the lesson start: free",
    informativeMessage:
      "Changes or cancellations made at least 72 hours prior to the lesson start: free",
    changeFee: 0,
  }),
  "not-initiated-by-student": (): CancelPolicy => ({
    message: "Changes or cancellations not initiated by student: free",
    changeFee: 0,
  }),
}

export function getCancelPolicyKeyPoints() {
  return Object.values(CANCEL_POLICIES)
    .map((policyFn) => policyFn(0).informativeMessage ?? "")
    .filter(Boolean)
}

export function getCancelPolicy({
  startTime,
  price,
  initiatedBy,
  timeZone,
}: {
  startTime: number | Date
  price: number
  initiatedBy: GQLInitiatedBy
  timeZone: string
}) {
  if (initiatedBy !== GQLInitiatedBy.Student) {
    return CANCEL_POLICIES["not-initiated-by-student"]()
  }

  const hoursAway = dayjs
    .duration(
      dayjs(startTime).tz(timeZone).diff(dayjs.tz(new Date(), timeZone)),
    )
    .asHours()

  switch (true) {
    case hoursAway < 24:
      return CANCEL_POLICIES["within-24-hours"](price)
    case hoursAway < 72:
      return CANCEL_POLICIES["within-72-to-24-hours"](price)
    default:
      return CANCEL_POLICIES["over-72-hours"]()
  }
}
